import React, { useState, useEffect, useCallback } from 'react';
import Grid from '@mui/material/Grid2';

import { 
  AttachedTable,
  Table,
} from "@dndModels";
import { FEATURE_SOURCE_TYPE_OPTIONS } from "@dndModels/Constants";

import API from "@dndUtilities/system_api";

import SelectStringBox from "@components/input/selects/SelectStringBox";
import SelectObjectBox from "@components/input/selects/SelectObjectBoxSimple";
import LoadingBox from "@components/input/LoadingBox";
import { DataType } from '@dndModels/Types';

interface Props {
  name?: string;
  type: DataType | "Attached" | "";
  id: string;
  field: string;
  onChange: (changed: { type: string, id: string, field: string }) => void; 
  disabled?: boolean;
  log_it?: boolean;
  attached_table?: AttachedTable;
}

const api = API.getInstance();

export default function SelectTableBox({
  name = "",
  type,
  id,
  field,
  onChange,
  disabled = false,
  log_it = false,
  attached_table
}: Props) {
  const [fetched, setFetched] = useState(false);
  const [loading, setLoading] = useState(false);
  const [use_type, setUseType] = useState(type);
  const [use_id, setUseId] = useState(id);
  const [use_field, setUseField] = useState(field);
  const [tables, setTables] = useState<Table[]>([]);
  const [reloading, setReloading] = useState(false);
  
  const loadTables = useCallback( async () => {
    const res = await api.getObjects({ data_type: "table" });
    setTables(res as Table[]);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (!fetched) {
      setFetched(true);
      setLoading(true);
      setTimeout(() => {
        loadTables();
      }, 100);
    }
  }, [fetched, loadTables]);

  useEffect(() => {
    if (reloading) {
      setReloading(false);
    }
  }, [reloading]);

  const renderTableFieldOptions = () => {
    let table: Table | AttachedTable | undefined = tables.find(o => o.for_id === use_id);
    if (!table) {
      table = attached_table;
    }
    if (table) {
      return (
        <Grid size={6}>
          <SelectStringBox
            name="Table Field"
            value={`${use_field}`}
            options={ table.fields }
            disabled={disabled}
            auto_sort
            onChange={(changed: string) => {
              // debugger;
              setUseField(changed);
              onChange({ type: use_type, id: use_id, field: changed });
            }}
          />
        </Grid>
      );
    } 
    return null;
  }

  if (!fetched || loading) {
    return (<LoadingBox />);
  } else {
    return (
      <Grid container spacing={0} direction="row">
        { name !== "" && 
          <Grid size={12} className="header">{ name }</Grid> 
        }
        <Grid size={12}>
          <SelectStringBox
            name="Table Type"
            value={`${use_type}`}
            options={attached_table ? [...FEATURE_SOURCE_TYPE_OPTIONS,{ id: "Attached", text: "Attached"}] : FEATURE_SOURCE_TYPE_OPTIONS}
            disabled={disabled}
            onChange={(changed: DataType | "Attached") => {
              setUseType(changed);
              if (changed === "Attached") {
                setUseId("Attached");
                onChange({ type: changed, id: "Attached", field: use_field });
              } else {
                setUseId("");
                onChange({ type: changed, id: "", field: use_field });
              }
              onChange({ type: changed, id: use_id, field: use_field });
              setReloading(true);
            }}
          />
        </Grid>
        { !reloading && use_type !== "" &&
          <>
            { use_type === "Attached" ?
              <Grid size={6}>
                { renderTableFieldOptions() }
              </Grid>
            : 
              <>
                <Grid size={6}>
                  <SelectObjectBox
                    name="Table"
                    data_type={use_type}
                    value={use_id}
                    disabled={disabled}
                    auto_sort
                    // log_it
                    dominant_only
                    show_details
                    onChange={(changed: string) => {
                      setUseId(changed);
                      onChange({ type: use_type, id: changed, field: use_field });
                    }}
                  />
                </Grid>
                { renderTableFieldOptions() }
              </>
            }
          </>
        }
      </Grid>
    );
  }
}
